import { Controller } from "@hotwired/stimulus"
import { formatPln, getAllJSON, getJSON, listResults } from "../api.js"

// Mapowanie wartości property_type z API na etykiety PL (ins-forms seq 7).
const PROPERTY_TYPE_LABELS = { mieszkanie: "Mieszkanie", dom: "Dom", inne: "Inne" }

// Katalog (panel klienta): wiersze tabeli + sidebar kategorii + filtry z DRF.
//
// Paginacja/filtry są SERVER-SIDE: stan żyje w parametrach URL (?subcategory=&voivodeship=
// &county=&category=&lead_type=&page=&favorites=), a kontroler przekazuje je do /api/catalog/
// i renderuje wynik.
// Linki paginacji/sidebara to zwykłe hrefy (przeładowanie strony) — progressive enhancement.
// Serializer katalogu jest 0-PII; wypełniamy wyłącznie textContent (bez innerHTML).
//
// Kaskada filtrów (E2): sidebar jest hierarchiczny — nagłówek kategorii to link (?category=slug)
// ORAZ przycisk-toggle accordion (caret). Podkategorie filtrują precyzyjniej (?subcategory=slug).
// Kliknięcie podkategorii usuwa filtr kategorii (bardziej precyzyjny wybór).
export default class extends Controller {
  static targets = ["title", "count", "rows", "status", "pagination", "pageInfo", "pageLinks",
                    "catlist", "voivFilter", "countyFilter", "typeFilter", "sortFilter", "dirFilter",
                    "subcatInput", "categoryInput", "favoritesLink", "favoritesToggle", "favoritesInput", "allLink"]
  static values = {
    url: String,               // /api/catalog/
    categoriesUrl: String,     // /api/categories/
    subcategoriesUrl: String,  // /api/subcategories/
    voivodeshipsUrl: String,   // /api/voivodeships/
    countiesUrl: String,       // /api/counties/
    vertical: String,          // slug pionu (np. "ubezpieczenia") — dołączany do każdego XHR katalogu/kategorii
    backorderUrl: String,      // /ulubione/ — link w pustym stanie Ulubionych
    subscriptionsUrl: String,  // /api/backorder-subscriptions/ — ulubione do serduszek w sidebarze
    purchasingEnabled: { type: Boolean, default: true }, // false = personel jeszcze nie zweryfikował klienta — blokuje "Dodaj"
  }

  static PAGE_SIZE = 25
  static FILTER_KEYS = ["subcategory", "category", "voivodeship", "county", "lead_type", "sort", "dir", "favorites"]

  connect() {
    this.params = new URLSearchParams(window.location.search)
    this.#initFilters()
    this.#setupFavorites()
    this.#loadSidebar()
    this.#loadVoivodeships()
    this.#loadCounties()
    this.#loadLeads()
  }

  // --- prywatne ---

  #initFilters() {
    if (this.hasSubcatInputTarget)    this.subcatInputTarget.value    = this.params.get("subcategory") || ""
    if (this.hasCategoryInputTarget)  this.categoryInputTarget.value  = this.params.get("category") || ""
    if (this.hasTypeFilterTarget)     this.typeFilterTarget.value     = this.params.get("lead_type") || ""
    if (this.hasSortFilterTarget)     this.sortFilterTarget.value     = this.params.get("sort") || "fresh"
    if (this.hasDirFilterTarget)      this.dirFilterTarget.value      = this.params.get("dir") || ""
  }

  async #loadLeads() {
    const query = new URLSearchParams()
    for (const key of [...this.constructor.FILTER_KEYS, "page"]) {
      const value = this.params.get(key)
      if (value) query.set(key, value)
    }
    if (this.verticalValue) query.set("vertical", this.verticalValue)
    let data
    try {
      data = await getJSON(query.toString() ? `${this.urlValue}?${query}` : this.urlValue)
    } catch {
      this.#status("Nie udało się załadować leadów. Odśwież stronę.")
      return
    }
    this.#renderRows(listResults(data))
    if (this.hasCountTarget) this.countTarget.textContent = data.count ?? "0"
    this.#renderPagination(data)
  }

  #renderRows(items) {
    this.rowsTarget.textContent = ""
    if (!items.length) {
      if (this.params.get("favorites") === "1") {
        this.#statusFavoritesEmpty()
      } else {
        this.#status("Brak leadów spełniających kryteria.")
      }
      return
    }
    this.#status(null)
    const template = document.getElementById("catalog-row-template")
    for (const item of items) {
      const row = template.content.firstElementChild.cloneNode(true)
      row.dataset.leadId = item.id
      row.classList.toggle("is-selected", item.in_cart)

      // Checkbox = zaznaczenie do bulk-add (stan UŻYTKOWNIKA) — celowo NIE pre-zaznaczamy
      // pozycji będących w koszyku, inaczej bulkbar liczyłby je jako "wybrane do dodania".
      const checkbox = row.querySelector("input.ls-checkbox")
      checkbox.value = item.id

      row.querySelector('[data-field="cat"]').textContent = item.category_path
      row.querySelector('[data-field="code"]').textContent = item.code
      row.querySelector('[data-field="note"]').textContent = item.note_preview
      row.querySelector('[data-field="region"]').textContent = item.region

      const badge = row.querySelector('[data-field="badge"]')
      // Potencjał ma dziś tylko dwie wartości (gorący/ciepły) — nadawane automatycznie.
      // Wariant "new" został jako NEUTRALNY styl dla nieznanej wartości z API, nie jako kategoria.
      const kind = item.score === "hot" || item.score === "warm" ? item.score : "new"
      badge.className = `ls-badge ls-badge--${kind}`
      badge.textContent = item.score_label

      // Pokrycie tagowe kafelka (Task 2): ile pasujących tag-listingów złożyło się na
      // price_pln (baza x coverage). Badge tylko gdy > 1 — coverage=1/0 to zwykła cena bazowa.
      const coverageBadge = row.querySelector('[data-field="coverage"]')
      if (coverageBadge) {
        if (item.coverage > 1) {
          coverageBadge.hidden = false
          coverageBadge.textContent = `x${item.coverage}`
        } else {
          coverageBadge.hidden = true
          coverageBadge.textContent = ""
        }
      }

      // price_pln to JUŻ cena kafelka (baza x coverage) — bez matematyki po stronie klienta.
      const price = formatPln(item.price_pln)
      row.querySelector('[data-field="price"]').textContent = price

      const previewBtn = row.querySelector("[data-action='preview#open']")
      Object.assign(previewBtn.dataset, {
        previewId: item.id,
        previewCode: item.code,
        previewCat: item.category_path,
        previewRegion: item.region,
        previewScore: item.score_label,
        previewScoreKind: kind,
        previewNote: item.note,
        previewPrice: price,
      })

      const addBtn = row.querySelector(".ls-add-btn")
      // Kafelek (lead, podkategoria) — Plan 1 zmienił cart/add na {lead, subcategory} (pk),
      // bo item.id to tylko JEDEN reprezentatywny tag-listing z wielu możliwych dla tego
      // kafelka; backend (reserve_tile) sam dobiera właściwy listing wg tagów org.
      addBtn.setAttribute("data-cart-lead-param", item.lead_id)
      addBtn.setAttribute("data-cart-subcategory-param", item.subcategory_id)
      // data-cart-id-param zostaje: to WYŁĄCZNIE klucz wyszukiwania dla innych kontrolerów
      // (preview_controller.js#addToCart deleguje klik na ten przycisk; lead_table_controller.js
      // #addSelected odbija stan po bulk-add) — żaden z nich nie czyta go do treści POST-a.
      addBtn.setAttribute("data-cart-id-param", item.id)
      if (item.in_cart) {
        addBtn.classList.add("is-in-cart")
        addBtn.querySelector('[data-field="addIcon"]').textContent = "✓"
        addBtn.querySelector("[data-cart-label]").textContent = "W koszyku"
      }
      // Konto klienta jeszcze nie zweryfikowane (purchasing_enabled=False, patrz baner
      // wyżej na stronie) — backend i tak 403-uje add/checkout, ale przycisk nie może
      // wyglądać na klikalny. Usuwamy data-action (nie tylko disabled=true), więc Stimulus
      // nie ma czego routować niezależnie od tego, jak przeglądarka traktuje kliknięcie na
      // disabled — blokada nie zależy od wsparcia disabled dla klików w danym środowisku.
      if (!this.purchasingEnabledValue) {
        addBtn.disabled = true
        addBtn.removeAttribute("data-action")
        addBtn.title = "Konto nie zostało jeszcze zweryfikowane — skontaktuj się z nami, aby aktywować zakupy."
      }

      // ins-forms seq 7: profil ubezpieczeniowy — BUR leady mają profile_summary=null.
      // Gałąź null-check chroni istniejące BUR karty przed jakąkolwiek zmianą zachowania.
      const profileEl = row.querySelector('[data-field="profile"]')
      if (profileEl && item.profile_summary !== null && item.profile_summary !== undefined) {
        this.#renderProfileSummary(profileEl, item.profile_summary)
      }

      // ins-slots#16: mode picker — only offer modes that the API says are actually available.
      //
      // Four card states for slot-capable (insurance) leads:
      //   State 1 — both open     (slot_available=T, exclusivity_available=T):
      //             show picker with both options, default to exclusivity.
      //   State 2 — slot only     (slot_available=T, exclusivity_available=F):
      //             show picker with exclusivity option HIDDEN, default to slot.
      //   State 3 — excl only/BUR (slot_available=F, exclusivity_available=T):
      //             no picker (single implicit mode), add button works normally — preserves
      //             the pre-slots BUR contract (no mode in POST, backend defaults to exclusivity).
      //   State 4 — sold out      (slot_available=F, exclusivity_available=F):
      //             disable the add control entirely; do not invite a purchase that will fail.
      //
      // WHY hidden (not disabled) for the blocked exclusivity option in State 2:
      //   A disabled button with a price label still invites inquiry ("why can't I click this?"),
      //   but we cannot explain the reason without leaking that other orgs have paid slots (PII).
      //   Hiding is honest — the picker shows only what the buyer can actually do.

      // State 4: neither mode is available — disable add entirely.
      if (!item.slot_available && item.exclusivity_available === false) {
        addBtn.disabled = true
        addBtn.removeAttribute("data-action")
        addBtn.title = "Ten lead jest niedostępny — wszystkie miejsca zostały zajęte."
      }

      // States 1 & 2: slots are available — show the mode picker.
      if (item.slot_available) {
        const modePicker = row.querySelector('[data-field="modePicker"]')
        if (modePicker) {
          modePicker.hidden = false
          const exclPriceEl = row.querySelector('[data-field="exclPrice"]')
          if (exclPriceEl && item.exclusivity_price_pln != null) {
            exclPriceEl.textContent = formatPln(item.exclusivity_price_pln)
          }
          const slotPriceEl = row.querySelector('[data-field="slotPrice"]')
          if (slotPriceEl && item.slot_price_pln != null) {
            slotPriceEl.textContent = formatPln(item.slot_price_pln)
          }
          const slotsRemEl = row.querySelector('[data-field="slotsRemaining"]')
          if (slotsRemEl) {
            slotsRemEl.textContent = `${item.slots_remaining} dostępne`
          }

          const exclOptEl = row.querySelector('[data-field="modeOptExcl"]')

          if (item.exclusivity_available === false) {
            // State 2: exclusivity blocked by paid slots — hide excl option, default to slot.
            if (exclOptEl) {
              exclOptEl.hidden = true
              exclOptEl.classList.remove("is-active")
            }
            const slotOptEl = row.querySelector('[data-field="modeOptSlot"]')
            if (slotOptEl) slotOptEl.classList.add("is-active")
            addBtn.setAttribute("data-cart-mode-param", "slot")
          } else {
            // State 1: both modes available — default to exclusivity (template is-active stays).
            addBtn.setAttribute("data-cart-mode-param", "exclusivity")
          }
        }
      }

      this.rowsTarget.appendChild(row)
    }
  }

  // Renderuje pola profilu ubezpieczeniowego w kontenerze karty.
  // Klucz days_to_policy_end: OBECNOŚĆ w obiekcie jest warunkiem renderowania (nie wartość),
  // bo 0 oznacza "polisa kończy się dziś" i jest prawidłową wartością do wyświetlenia.
  // has_insurance: null = "nie podano" — pominięte; true/false → "Tak"/"Nie".
  // BUR: profile_summary === null → metoda nie jest wywoływana (gałąź w #renderRows).
  #renderProfileSummary(container, summary) {
    container.textContent = ""

    const addItem = (label, value) => {
      const span = document.createElement("span")
      span.className = "ls-lead-row__note-text"
      span.textContent = `${label}: ${value}`
      container.appendChild(span)
    }

    const { form_type } = summary

    if (form_type === "communication") {
      if ("days_to_policy_end" in summary) {
        addItem("Dni do końca polisy", summary.days_to_policy_end)
      }
    } else if (form_type === "life") {
      if (summary.has_insurance !== null && summary.has_insurance !== undefined) {
        addItem("Czy ma ubezpieczenie na życie", summary.has_insurance ? "Tak" : "Nie")
      }
    } else if (form_type === "property") {
      if (summary.property_type) {
        addItem("Typ nieruchomości", PROPERTY_TYPE_LABELS[summary.property_type] ?? summary.property_type)
      }
    } else if (form_type === "property_life") {
      if (summary.property_type) {
        addItem("Typ nieruchomości", PROPERTY_TYPE_LABELS[summary.property_type] ?? summary.property_type)
      }
      if (summary.has_insurance !== null && summary.has_insurance !== undefined) {
        addItem("Czy ma ubezpieczenie na życie", summary.has_insurance ? "Tak" : "Nie")
      }
    }

    container.hidden = container.children.length === 0
  }

  #renderPagination(data) {
    if (!this.hasPaginationTarget) return
    const hasPages = Boolean(data.next || data.previous)
    this.paginationTarget.hidden = !hasPages
    if (!hasPages) return

    const page = Number(this.params.get("page") || 1)
    const shown = listResults(data).length
    const start = (page - 1) * this.constructor.PAGE_SIZE + 1
    this.pageInfoTarget.textContent = `${start}–${start + shown - 1} z ${data.count}`

    this.pageLinksTarget.textContent = ""
    const makeLink = (label, targetPage, enabled) => {
      const a = document.createElement("a")
      a.className = `ls-page${enabled ? "" : " is-disabled"}`
      a.textContent = label
      if (enabled) a.href = this.#pageHref(targetPage)
      this.pageLinksTarget.appendChild(a)
    }
    makeLink("← Poprzednia", page - 1, Boolean(data.previous))
    const current = document.createElement("span")
    current.className = "ls-page is-active"
    current.textContent = page
    this.pageLinksTarget.appendChild(current)
    makeLink("Następna →", page + 1, Boolean(data.next))
  }

  #pageHref(page) {
    const query = new URLSearchParams()
    for (const key of this.constructor.FILTER_KEYS) {
      const value = this.params.get(key)
      if (value) query.set(key, value)
    }
    if (page > 1) query.set("page", page)
    const text = query.toString()
    return text ? `?${text}` : window.location.pathname
  }

  async #loadSidebar() {
    if (!this.hasCatlistTarget) return
    let categories
    let subcategories
    let subscriptions = []
    try {
      ;[categories, subcategories, subscriptions] = await Promise.all([
        getAllJSON(this.verticalValue ? `${this.categoriesUrlValue}?vertical=${this.verticalValue}` : this.categoriesUrlValue),
        getAllJSON(this.verticalValue ? `${this.subcategoriesUrlValue}?vertical=${this.verticalValue}` : this.subcategoriesUrlValue),
        // Ulubione (serduszka): ładuj tylko gdy mamy URL subskrypcji.
        // Błąd subskrypcji nie blokuje sidebara — łapiemy cicho.
        this.hasSubscriptionsUrlValue
          ? getAllJSON(this.subscriptionsUrlValue).catch(() => [])
          : Promise.resolve([]),
      ])
    } catch {
      return // sidebar zostaje z samym "Wszystkie leady"
    }
    // Zbiór id podkategorii z aktywną subskrypcją — do inicjalizacji serduszek
    const favoriteIds = new Set(subscriptions.map((s) => s.subcategory))

    const activeSubcat = this.params.get("subcategory") || ""
    const activeCat    = this.params.get("category") || ""
    const isFavorites  = this.params.get("favorites") === "1"

    // allLink aktywny = brak podkategorii, brak kategorii, brak favorites
    if (this.hasAllLinkTarget) {
      this.allLinkTarget.classList.toggle("is-active", !activeSubcat && !activeCat && !isFavorites)
    }

    const subsByCategory = new Map()
    for (const sub of subcategories) {
      const list = subsByCategory.get(sub.category) ?? []
      list.push(sub)
      subsByCategory.set(sub.category, list)
    }

    for (const category of categories) {
      const subs         = subsByCategory.get(category.id) ?? []
      // Kategoria aktywna: wybrana jawnie (?category=slug) LUB ma aktywną podkategorię
      const catIsActive  = activeCat === category.slug
      const hasActiveSub = subs.some((sub) => sub.slug === activeSubcat)
      const expanded     = catIsActive || hasActiveSub

      // Tytuł strony = aktywna podkategoria (precyzyjniej) LUB aktywna kategoria
      if (catIsActive && this.hasTitleTarget) this.titleTarget.textContent = category.name
      if (activeSubcat && hasActiveSub) {
        const activeSub = subs.find((sub) => sub.slug === activeSubcat)
        if (activeSub && this.hasTitleTarget) this.titleTarget.textContent = activeSub.name
      }

      const group = document.createElement("div")
      group.className = "ls-cat-group"

      // Nagłówek kategorii: link do filtrowania po kategorii + przycisk-toggle accordion (caret).
      // Klik na nazwę → nawiguje do ?category=slug (wszystkie podkategorie tej kategorii).
      // Klik na caret → rozwija/zwija listę podkategorii bez nawigacji.
      const header = document.createElement("div")
      header.className = "ls-cat-header"

      const nameLink = document.createElement("a")
      nameLink.className = `ls-cat-name${catIsActive ? " is-active" : ""}`
      nameLink.href = this.#categoryHref(category.slug)
      nameLink.textContent = category.name
      header.appendChild(nameLink)

      const toggle = document.createElement("button")
      toggle.type = "button"
      toggle.className = "ls-cat-toggle"
      toggle.setAttribute("data-action", "catalog#toggleCategory")
      toggle.setAttribute("aria-expanded", String(expanded))
      const caret = document.createElement("span")
      caret.className = "ls-cat-caret"
      caret.textContent = expanded ? "▾" : "▸"
      toggle.appendChild(caret)
      header.appendChild(toggle)

      group.appendChild(header)

      // Kontener zwijany z podkategoriami (linki server-side jak dotąd).
      const subsBox = document.createElement("div")
      subsBox.className = "ls-cat-subs"
      subsBox.hidden = !expanded
      for (const sub of subs) {
        // Wiersz podkategorii: link + serduszko (akcja „dodaj do ulubionych").
        const subRow = document.createElement("div")
        subRow.className = "ls-catlink-row"

        const link = document.createElement("a")
        link.className = `ls-catlink ls-catlink--sub${activeSubcat === sub.slug ? " is-active" : ""}`
        link.href = this.#subcategoryHref(sub.slug)
        link.textContent = sub.name
        subRow.appendChild(link)

        // Serduszko — obsługiwane przez `favorite-heart` controller.
        const heart = document.createElement("button")
        heart.type = "button"
        heart.className = "ls-cat-heart"
        heart.setAttribute("data-controller", "favorite-heart")
        heart.setAttribute("data-favorite-heart-subcategory-id-value", String(sub.id))
        heart.setAttribute("data-favorite-heart-active-value", String(favoriteIds.has(sub.id)))
        heart.setAttribute("data-action", "favorite-heart#toggle")
        subRow.appendChild(heart)

        subsBox.appendChild(subRow)
      }
      group.appendChild(subsBox)
      this.catlistTarget.appendChild(group)
    }
  }

  // ins-slots#7: klik w przycisk trybu (Wyłączność / Slot) wewnątrz danego wiersza.
  // WHY: dane modePicker żyją w DOM wiersza — nie ma globalnego stanu trybu,
  // więc stary wybór z innej karty nie może przypadkowo trafić do POST-a innej karty.
  // WHY update data-cart-mode-param: cart#add odczytuje tryb z params Stimulus;
  // aktualizacja atrybutu na przycisku Dodaj (nie metodą, lecz przez DOM) to jedyne
  // miejsce, które przeżyje re-render strony bez czyszczenia Map<>/closure'a.
  selectMode(event) {
    const btn = event.currentTarget
    const row = btn.closest(".ls-lead-row")
    if (!row) return
    const mode = btn.dataset.mode
    if (!mode) return

    // Przesuń is-active na kliknięty wariant; zdejmij z pozostałych.
    row.querySelectorAll('[data-field="modePicker"] .ls-mode-opt').forEach((opt) => {
      opt.classList.toggle("is-active", opt.dataset.mode === mode)
    })

    // Zaktualizuj tryb w przycisku Dodaj, żeby cart#add wysłał właściwy mode do POST-a.
    const addBtn = row.querySelector(".ls-add-btn")
    if (addBtn) {
      addBtn.setAttribute("data-cart-mode-param", mode)
    }
  }

  // Accordion: klik w przycisk caret rozwija/zwija podkategorie kategorii (bez nawigacji).
  toggleCategory(event) {
    const btn = event.currentTarget
    const group = btn.closest(".ls-cat-group")
    const subsBox = group.querySelector(".ls-cat-subs")
    const caret = btn.querySelector(".ls-cat-caret")
    const willExpand = subsBox.hidden
    subsBox.hidden = !willExpand
    btn.setAttribute("aria-expanded", String(willExpand))
    if (caret) caret.textContent = willExpand ? "▾" : "▸"
  }

  // Link kategorii: zachowuje filtry (woj., powiat, typ, sort, favorites), czyści subcat+page.
  #categoryHref(slug) {
    const query = new URLSearchParams()
    for (const key of this.constructor.FILTER_KEYS) {
      if (key === "category" || key === "subcategory") continue
      const value = this.params.get(key)
      if (value) query.set(key, value)
    }
    query.set("category", slug)
    return `?${query}`
  }

  // Link podkategorii: zachowuje filtry; usuwa filtr kategorii (podkat jest precyzyjniejszy).
  #subcategoryHref(slug) {
    const query = new URLSearchParams()
    for (const key of this.constructor.FILTER_KEYS) {
      if (key === "subcategory" || key === "category") continue
      const value = this.params.get(key)
      if (value) query.set(key, value)
    }
    query.set("subcategory", slug)
    return `?${query}`
  }

  async #loadVoivodeships() {
    if (!this.hasVoivFilterTarget) return
    let voivodeships
    try {
      voivodeships = await getAllJSON(this.voivodeshipsUrlValue)
    } catch {
      return
    }
    const active = this.params.get("voivodeship") || ""
    for (const voivodeship of voivodeships) {
      const option = document.createElement("option")
      option.value = voivodeship.id
      option.textContent = voivodeship.name
      this.voivFilterTarget.appendChild(option)
    }
    this.voivFilterTarget.value = active
  }

  async #loadCounties() {
    if (!this.hasCountyFilterTarget) return
    // Powiat zależy od województwa: bez wybranego województwa select jest wyłączony.
    const voivodeship = this.params.get("voivodeship") || ""
    if (!voivodeship) {
      this.countyFilterTarget.disabled = true
      return
    }
    let counties
    try {
      counties = await getAllJSON(`${this.countiesUrlValue}?voivodeship=${voivodeship}`)
    } catch {
      return
    }
    for (const county of counties) {
      const option = document.createElement("option")
      option.value = county.id
      option.textContent = county.name
      this.countyFilterTarget.appendChild(option)
    }
    this.countyFilterTarget.value = this.params.get("county") || ""
    this.countyFilterTarget.disabled = false
  }

  // Ulubione: synchronizuje href i is-active dla sidebar + pasek filtrów.
  // Buduje href toggle (dodaje/usuwa favorites=1, zachowuje pozostałe filtry).
  #setupFavorites() {
    const isActive = this.params.get("favorites") === "1"
    const query = new URLSearchParams()
    for (const key of this.constructor.FILTER_KEYS) {
      if (key === "favorites") continue
      const value = this.params.get(key)
      if (value) query.set(key, value)
    }
    if (!isActive) query.set("favorites", "1")
    const href = query.toString() ? `?${query}` : window.location.pathname

    for (const target of [
      this.hasFavoritesLinkTarget && this.favoritesLinkTarget,
      this.hasFavoritesToggleTarget && this.favoritesToggleTarget,
    ].filter(Boolean)) {
      target.href = href
      target.classList.toggle("is-active", isActive)
    }

    // Ukryty input w formularzu filtrów — zachowuje favorites przy submit (zmiana np. województwa).
    if (this.hasFavoritesInputTarget) {
      this.favoritesInputTarget.value = isActive ? "1" : ""
    }
  }

  // Pusty stan specyficzny dla trybu Ulubionych: komunikat + link do powiadomień (XSS-safe DOM).
  #statusFavoritesEmpty() {
    if (!this.hasStatusTarget) return
    this.statusTarget.hidden = false
    this.statusTarget.textContent = ""
    const text = document.createElement("span")
    text.textContent = "Brak ulubionych — dodaj powiadomienia, aby zbudować listę. "
    const link = document.createElement("a")
    link.href = this.hasBackorderUrlValue ? this.backorderUrlValue : "/powiadomienia/"
    link.textContent = "Przejdź do powiadomień"
    this.statusTarget.append(text, link)
  }

  #status(message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.hidden = message === null
    if (message !== null) this.statusTarget.textContent = message
  }
}
